// ============================================================
// src/hooks/useTripMode.js
//
// WHAT THIS FILE DOES
//
// Manages trips stored in the notification_preferences.trips
// column. A trip represents a period when the performer is
// traveling away from home. When a trip is active on a gig
// day, the app routes from the hotel/stay address instead of
// home.
//
// TWO TRIP TYPES
//
// ROUND TRIP (existing shape -- fully supported)
// {
//   id, trip_type: 'round_trip',
//   destination_label, stay_address, stay_lat, stay_lng,
//   transport_mode: 'flight' | 'drive' | 'train' | 'bus',
//   depart_home_at, arrive_dest_at,
//   depart_dest_at, arrive_home_at,
//   dest_timezone,
//   origin_terminal_address/lat/lng/label,
//   dest_terminal_address/lat/lng/label,
// }
//
// MULTI-LEG TOUR (new)
// {
//   id, trip_type: 'multi_leg',
//   destination_label: "Midwest Tour 2025",
//   legs: [
//     {
//       id,
//       from_label, to_label,
//       stay_address, stay_lat, stay_lng,
//       transport_mode,
//       depart_at,    -- when the performer leaves from_label
//       arrive_at,    -- when the performer arrives at to_label
//       dest_timezone,
//       origin_terminal_address/lat/lng/label,
//       dest_terminal_address/lat/lng/label,
//     },
//     ...more legs
//   ]
// }
//
// ACTIVE TRIP LOGIC
//
// Round trip: active on a date when:
//   arrive_dest_at <= date < depart_dest_at
//
// Multi-leg: leg N covers dates from its arrive_at through
// the depart_at of leg N+1. The last leg covers arrive_at
// through arrive_at + 1 day.
//
// getActiveTripForDate() returns the trip object (augmented
// for multi-leg) so GigDayPreview does not need to know which
// trip type it is looking at.
// ============================================================

import { useState, useEffect, useCallback, useRef } from 'react'
import { supabase } from '../lib/supabaseClient'
import { v4 as uuidv4 } from 'uuid'
// uuidv4 = generates a random unique ID for each new trip or leg


// ============================================================
// TERMINAL CACHE
//
// Airports, train stations, and bus terminals that the user
// has entered before are cached in localStorage so the form
// can auto-complete them next time.
//
// The cache is a plain object keyed by lowercase terminal name.
// ============================================================

const TERMINAL_CACHE_KEY = 'dppt_terminal_cache'
// the key used to store the cache in localStorage

function loadTerminalCache() {
  try {
    return JSON.parse(localStorage.getItem(TERMINAL_CACHE_KEY) || '{}')
    // JSON.parse turns the stored string back into an object
    // || '{}' = if nothing is stored yet, parse an empty object
  } catch {
    return {}
    // JSON.parse can throw if the stored value is corrupt
    // return empty cache so the app keeps working
  }
}

function saveTerminalCache(cache) {
  try {
    localStorage.setItem(TERMINAL_CACHE_KEY, JSON.stringify(cache))
    // JSON.stringify converts the object to a string for storage
  } catch {
    // localStorage can throw if the browser is in private mode
    // or storage is full. Silently ignore -- caching is not critical.
  }
}


// ============================================================
// useTerminalCache
//
// A small hook that exposes the terminal cache to components.
// Provides addTerminal (save a new entry) and
// lookupTerminals (search by partial name).
// ============================================================
export function useTerminalCache() {
  const [cache, setCache] = useState(loadTerminalCache)
  // load the initial cache from localStorage on first render

  const addTerminal = useCallback((label, data) => {
    const key = label.toLowerCase().trim()
    // lowercase and trim so "O'Hare" and "o'hare" map to the same entry

    const updated = { ...loadTerminalCache(), [key]: { label, ...data } }
    // reload from localStorage first (not from state) to avoid stale data
    // then add the new entry
    // [key] uses the variable key as the object property name

    saveTerminalCache(updated)
    setCache(updated)
  }, [])

  const lookupTerminals = useCallback((query) => {
    const q = (query || '').toLowerCase().trim()
    // normalize the search query the same way we normalized keys

    if (!q) return []
    // empty query = return nothing (avoid showing all terminals at once)

    return Object.values(loadTerminalCache()).filter(t =>
      t.label && t.label.toLowerCase().includes(q)
      // Object.values = get all cached terminal objects as an array
      // .includes(q) = partial match so "ord" matches "O'Hare (ORD)"
    )
  }, [])

  return { cache, addTerminal, lookupTerminals }
}


// ============================================================
// HELPERS
// ============================================================

// Extract just the "YYYY-MM-DD" date part from an ISO timestamp string.
// iso = something like "2025-06-01T20:00:00.000Z"
// returns "2025-06-01"
function dateOf(iso) {
  return iso ? iso.split('T')[0] : null
  // .split('T')[0] = everything before the T = the date portion
}


// Add n days to a date string and return the new date string.
// dateStr = "2025-06-01", n = 3 --> "2025-06-04"
// Used to calculate the end of the last leg's stay window.
function addDays(dateStr, n) {
  if (!dateStr) return null
  const [y, m, d] = dateStr.split('-').map(Number)
  // split the date string into year, month, day numbers
  const dt = new Date(y, m - 1, d + n)
  // m - 1 because JavaScript months are 0-indexed
  // d + n adds the days (Date handles month/year overflow automatically)
  return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}-${String(dt.getDate()).padStart(2, '0')}`
  // .padStart(2, '0') adds a leading zero so months and days are always 2 digits
  // e.g. month 6 becomes "06"
}


// ============================================================
// getActiveLegForDate
//
// For a multi-leg trip, finds which leg's stay covers dateStr.
//
// A leg's stay window spans:
//   [arrive_at of this leg, depart_at of the next leg)
//
// The last leg's stay window spans:
//   [arrive_at, arrive_at + 1 day)
//
// Returns the matching leg object with _legIndex added,
// or null if no leg covers that date.
// ============================================================
export function getActiveLegForDate(trip, dateStr) {
  if (!trip?.legs?.length) return null
  // no legs = cannot check anything

  const legs = trip.legs

  for (let i = 0; i < legs.length; i++) {
    const stayStart = dateOf(legs[i].arrive_at)
    // the day the performer arrives at this stop
    if (!stayStart) continue
    // skip legs with no arrival time

    const nextDepart = i + 1 < legs.length ? dateOf(legs[i + 1].depart_at) : null
    // the day the performer leaves this stop (= when the next leg starts)
    // null for the last leg since there is no next leg

    const stayEnd = nextDepart || addDays(stayStart, 1)
    // if there is a next leg, stay ends the day that leg departs
    // if this is the last leg, stay ends 1 day after arrival

    if (dateStr >= stayStart && dateStr < stayEnd) {
      return { ...legs[i], _legIndex: i }
      // dateStr falls within this leg's stay window
      // spread the leg and add _legIndex so callers know which leg it is
    }
  }

  return null
  // no leg covers this date
}


// ============================================================
// getOriginTerminal
//
// Returns the terminal (airport, station, etc.) where the
// performer departs FROM for a given leg.
//
// If the leg has explicit origin_terminal_* fields, those win.
// If not, falls back to the PREVIOUS leg's dest_terminal_* fields
// because the performer's departure point for leg N is usually
// the same terminal they arrived at on leg N-1.
// ============================================================
export function getOriginTerminal(legs, legIndex) {
  const leg = legs[legIndex]
  if (!leg) return null

  if (leg.origin_terminal_address || leg.origin_terminal_lat) {
    // This leg has an explicit origin terminal defined
    return {
      address: leg.origin_terminal_address,
      lat:     leg.origin_terminal_lat,
      lng:     leg.origin_terminal_lng,
      label:   leg.origin_terminal_label,
    }
  }

  if (legIndex > 0) {
    // No explicit origin -- check the previous leg's destination terminal
    const prev = legs[legIndex - 1]
    if (prev?.dest_terminal_address || prev?.dest_terminal_lat) {
      return {
        address: prev.dest_terminal_address,
        lat:     prev.dest_terminal_lat,
        lng:     prev.dest_terminal_lng,
        label:   prev.dest_terminal_label,
      }
    }
  }

  return null
  // no terminal info available for this leg
}


// ============================================================
// getDestTerminal
//
// Returns the terminal where the performer ARRIVES for a leg.
// Returns null if the leg has no destination terminal fields.
// ============================================================
export function getDestTerminal(legs, legIndex) {
  const leg = legs[legIndex]
  if (!leg) return null

  if (leg.dest_terminal_address || leg.dest_terminal_lat) {
    return {
      address: leg.dest_terminal_address,
      lat:     leg.dest_terminal_lat,
      lng:     leg.dest_terminal_lng,
      label:   leg.dest_terminal_label,
    }
  }

  return null
}


// ============================================================
// useTripMode HOOK
// ============================================================

export function useTripMode() {

  const [trips,   setTrips]   = useState([])
  // the list of trip objects for this user
  const [loading, setLoading] = useState(true)
  // true while trips are being fetched from Supabase
  const [error,   setError]   = useState(null)
  // null = no problem, string = last error message
  const [userId,  setUserId]  = useState(null)
  // the logged-in user's UUID, needed to query their trips

  // tripsRef always holds the most current trips array.
  // We use a ref (instead of just the state variable) inside
  // addTrip, updateTrip, and deleteTrip so those functions
  // always read the latest data even if React has not re-rendered
  // yet. Without this, rapid saves could cause one save to
  // overwrite another by working from a stale snapshot.
  const tripsRef = useRef(trips)
  useEffect(() => { tripsRef.current = trips }, [trips])
  // update the ref every time the trips state changes


  // Load the user's ID once when the hook first mounts
  useEffect(() => {
    supabase.auth.getUser().then(({ data: { user } }) => {
      if (user) setUserId(user.id)
    })
  }, [])


  // -------------------------------------------------------
  // fetchTrips
  //
  // Loads the trips array from the notification_preferences
  // table. Trips are stored as a JSONB column called "trips"
  // rather than in their own table, which keeps the schema
  // simple since the whole array always loads together.
  //
  // PGRST116 = "no rows found" = this user has no prefs row yet.
  //   That is fine -- just start with an empty trips array.
  // Any other error = real problem, store and log it.
  // -------------------------------------------------------
  const fetchTrips = useCallback(async () => {
    if (!userId) return
    // cannot fetch without knowing which user to query

    setLoading(true)
    setError(null)

    try {
      const { data, error: err } = await supabase
        .from('notification_preferences')
        .select('trips')
        // only select the trips column -- we do not need all the prefs
        .eq('user_id', userId)
        .single()
        // .single() returns the row directly (not wrapped in an array)

      if (err && err.code !== 'PGRST116') throw err
      // PGRST116 = no row yet = ok to start with empty trips
      // any other error code = real problem, throw it

      setTrips(data?.trips || [])
      // data?.trips = the trips array if the row exists, otherwise undefined
      // || [] = fall back to empty array if no trips saved yet

    } catch (err) {
      console.error('useTripMode: fetchTrips error:', err)
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }, [userId])
  // re-create this function if userId changes (e.g. after login)


  // Fetch trips whenever userId becomes available
  useEffect(() => {
    if (userId) fetchTrips()
  }, [userId, fetchTrips])


  // -------------------------------------------------------
  // persistTrips
  //
  // Writes the full trips array back to Supabase.
  //
  // WHY UPDATE INSTEAD OF UPSERT
  //   upsert's ON CONFLICT DO UPDATE can silently do nothing
  //   under some Row Level Security configurations, making it
  //   look like the save worked when it did not.
  //   UPDATE .eq('user_id') is unambiguous -- it targets exactly
  //   the row we already know exists.
  //   upsert is kept as a one-time fallback for users whose prefs
  //   row does not exist yet (first time saving).
  //
  // WHY NOT CALL fetchTrips AFTER SAVING
  //   fetchTrips sets loading=true, which triggers Calendar's
  //   loading guard and can unmount AddTripModal in the middle
  //   of a save, causing the form to re-hydrate with stale data.
  //   Instead, we use the DB-confirmed value from the .select()
  //   response to update local state directly.
  // -------------------------------------------------------
  const persistTrips = useCallback(async (newTrips) => {
    if (!userId) {
      console.warn('[useTripMode] persistTrips: no userId, aborting')
      return
    }

    console.log('[useTripMode] persistTrips: writing', newTrips.length, 'trip(s) for user', userId)

    // Try UPDATE first (the normal path for existing rows)
    const { data: updateData, error: updateErr } = await supabase
      .from('notification_preferences')
      .update({ trips: newTrips })
      // replace the trips column with the new array
      .eq('user_id', userId)
      // only update this user's row
      .select('trips')
      // return what was actually stored so we can confirm it

    console.log('[useTripMode] persistTrips UPDATE response:', { updateData, updateErr })

    if (updateErr) {
      // UPDATE failed -- fall back to upsert which can create the row
      console.warn('[useTripMode] persistTrips UPDATE failed, trying upsert:', updateErr.message)

      const { data: upsertData, error: upsertErr } = await supabase
        .from('notification_preferences')
        .upsert(
          { user_id: userId, trips: newTrips },
          { onConflict: 'user_id' }
          // onConflict = if a row with this user_id already exists, update it
        )
        .select('trips')

      console.log('[useTripMode] persistTrips UPSERT response:', { upsertData, upsertErr })

      if (upsertErr) throw upsertErr

      // Use the DB-confirmed value, or fall back to our local value
      // if RLS hides the row from the SELECT response
      setTrips(upsertData?.[0]?.trips ?? newTrips)
      return
    }

    // UPDATE succeeded. Use what the DB confirmed was stored.
    // If .select() returned nothing (can happen under some RLS configs),
    // fall back to the locally-computed newTrips value.
    const confirmed = updateData?.[0]?.trips ?? newTrips
    console.log('[useTripMode] persistTrips: confirmed trips from DB:', confirmed?.length, 'trip(s)')
    setTrips(confirmed)
  }, [userId])


  // -------------------------------------------------------
  // addTrip
  //
  // Creates a new trip with a random UUID and saves it.
  // tripData = the form values for the new trip
  // Returns the new trip object.
  // -------------------------------------------------------
  const addTrip = useCallback(async (tripData) => {
    const trip = { id: uuidv4(), ...tripData }
    // uuidv4() = a random unique ID like "f47ac10b-58cc-4372-a567-0e02b2c3d479"
    // ...tripData = copy all the form fields into the object
    await persistTrips([...tripsRef.current, trip])
    // ...tripsRef.current = keep all existing trips
    // trip = add the new one at the end
    return trip
  }, [persistTrips])


  // -------------------------------------------------------
  // updateTrip
  //
  // Merges changes into an existing trip and saves.
  //
  // For ROUND TRIP: spread all changes onto the trip root.
  //   updateTrip(id, { depart_dest_at: '2025-05-20T10:00' })
  //
  // For MULTI-LEG: pass the full updated legs array.
  //   updateTrip(id, { legs: updatedLegsArray })
  //   Any other top-level fields in the changes object are
  //   also applied (e.g. destination_label).
  //
  // We read from tripsRef instead of the trips state variable
  // to avoid stale closure issues with rapid saves.
  // -------------------------------------------------------
  const updateTrip = useCallback(async (id, changes) => {
    console.log('[useTripMode] updateTrip → id:', id, 'changes:', JSON.stringify(changes))

    const updated = tripsRef.current.map(t => {
      if (t.id !== id) return t
      // not this trip -- leave it unchanged

      if (changes.legs !== undefined) {
        // Multi-leg update: replace the legs array and apply any other
        // top-level changes at the same time
        const { legs, ...restChanges } = changes
        // destructure to separate legs from everything else
        // legs = the new legs array
        // restChanges = any other fields like destination_label
        return { ...t, ...restChanges, legs }
        // spread the original trip, then apply top-level changes,
        // then set the new legs array (legs wins over ...restChanges)
      }

      // Round trip or root-level update: merge changes directly
      return { ...t, ...changes }
    })

    console.log('[useTripMode] updateTrip → computed updated trips:', JSON.stringify(updated))
    await persistTrips(updated)
  }, [persistTrips])


  // -------------------------------------------------------
  // deleteTrip
  //
  // Removes a trip by its ID and saves the remaining list.
  // -------------------------------------------------------
  const deleteTrip = useCallback(async (id) => {
    await persistTrips(tripsRef.current.filter(t => t.id !== id))
    // .filter() keeps every trip whose id does NOT match the deleted one
    // result: all trips except the removed one
  }, [persistTrips])


  // -------------------------------------------------------
  // getActiveTripForDate
  //
  // Returns the trip that is active on a given dateStr
  // (formatted "YYYY-MM-DD"), or null if the performer is home.
  //
  // For multi-leg trips, augments the return value with the
  // active leg's fields so GigDayPreview can use stay_lat,
  // stay_lng, stay_address, and transport_mode without knowing
  // which trip type it is dealing with.
  // -------------------------------------------------------
  const getActiveTripForDate = useCallback((dateStr) => {
    if (!trips.length) return null
    // no trips saved = always at home

    for (const trip of trips) {

      // MULTI-LEG CHECK
      if (trip.trip_type === 'multi_leg') {
        const leg = getActiveLegForDate(trip, dateStr)
        // leg = the matching leg object, or null if no leg covers this date

        if (leg) {
          const legs = trip.legs
          const i    = leg._legIndex
          // _legIndex was added by getActiveLegForDate

          // Find the terminal the performer departs from when leaving this stay.
          // This is the origin terminal of the NEXT leg.
          const nextLegOrigin = i + 1 < legs.length
            ? getOriginTerminal(legs, i + 1)
            : null
          // null if this is the last leg (no departure terminal needed)

          // Return the trip augmented with the active leg's fields
          return {
            ...trip,
            // spread all top-level trip fields
            _activeLeg:         leg,
            // the full leg object for components that need leg-specific data
            stay_lat:           leg.stay_lat,
            stay_lng:           leg.stay_lng,
            stay_address:       leg.stay_address,
            transport_mode:     leg.transport_mode,
            // these fields match round trip shape so GigDayPreview works unchanged
            _arrivalTerminal:   getDestTerminal(legs, i),
            // the terminal where the performer arrived at this stop
            _departTerminal:    nextLegOrigin,
            // the terminal the performer leaves from when moving to the next stop
          }
        }
        continue
        // this multi-leg trip does not cover the date -- try the next trip
      }

      // ROUND TRIP CHECK (trip_type may be absent on older records)
      if (!trip.arrive_dest_at || !trip.depart_dest_at) continue
      // skip round trips with missing dates

      const arrived   = dateOf(trip.arrive_dest_at)
      // "YYYY-MM-DD" of the day the performer arrives at the destination
      const departing = dateOf(trip.depart_dest_at)
      // "YYYY-MM-DD" of the day the performer leaves to go home

      if (dateStr >= arrived && dateStr < departing) return trip
      // dateStr falls inside the stay window
      // >= arrived = include the arrival day
      // < departing = exclude the departure day (performer is traveling home)
    }

    return null
    // no trip covers this date -- performer is at home
  }, [trips])


  // Calculate today's date string once so activeTripToday is
  // always accurate when the hook returns.
  const todayStr = (() => {
    const now = new Date()
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`
    // build the date string manually to avoid timezone issues
    // toISOString() would give UTC midnight which can be the wrong day locally
  })()


  return {
    trips,               // Trip[] -- all trips for this user
    loading,             // boolean -- true while fetching
    error,               // string | null -- last error message
    addTrip,             // (tripData) => Promise<Trip>
    updateTrip,          // (id, changes) => Promise<void>
    deleteTrip,          // (id) => Promise<void>
    getActiveTripForDate,// (dateStr: 'YYYY-MM-DD') => Trip | null
    activeTripToday:     getActiveTripForDate(todayStr),
    // the active trip for today, or null if the performer is home
    refetch: fetchTrips, // () => Promise<void> -- manually reload trips
  }
}


// ============================================================
// getTransitBufferMinutes
//
// Returns how many extra minutes to add to the travel time
// for a given transport mode. Accounts for getting to/from
// the terminal and security or boarding wait times.
//
// flight  = 120 minutes (2 hours for airport security etc.)
// train   = 60 minutes (check-in and boarding)
// bus     = 60 minutes (same as train)
// drive   = 0 minutes (no terminal buffer needed)
// ============================================================
export function getTransitBufferMinutes(transportMode) {
  switch (transportMode) {
    case 'flight': return 120
    case 'train':  return 60
    case 'bus':    return 60
    default:       return 0
    // default covers 'drive' and any future modes
  }
}